import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/formatUtils';
import { 
  TrendingUp, TrendingDown, Scale, Percent, 
  Gift, RefreshCw, ShoppingCart, Wallet, Info
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

export const AdminProfitLossPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set default to current financial year
  useEffect(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const fyStartYear = currentMonth < 3 ? currentYear - 1 : currentYear;
    setStartDate(formatLocalDate(new Date(fyStartYear, 3, 1)));
    setEndDate(formatLocalDate(now));
  }, []);

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    
    setLoading(true);
    try {
      const response = await adminAPI.getProfitLossReport(startDate, endDate);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load P&L report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (startDate && endDate) {
      fetchReport();
    }
  }, [fetchReport, startDate, endDate]);

  const formatGrams = (value) => {
    return `${(value || 0).toFixed(6)} g`;
  };

  const applyPreset = (preset) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    switch (preset) {
      case 'today':
        setStartDate(formatLocalDate(now));
        setEndDate(formatLocalDate(now));
        break;
      case 'this_month':
        setStartDate(formatLocalDate(new Date(currentYear, currentMonth, 1)));
        setEndDate(formatLocalDate(now));
        break;
      case 'this_fy':
        const fyStartYear = currentMonth < 3 ? currentYear - 1 : currentYear;
        setStartDate(formatLocalDate(new Date(fyStartYear, 3, 1)));
        setEndDate(formatLocalDate(now));
        break;
      default:
        break;
    }
  };

  const netProfitGrams = report?.net_profit?.grams || 0;

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-pnl-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <div>
              <h1 className="font-serif text-xl text-navy font-semibold">Profit & Loss Statement</h1>
              <p className="text-xs text-muted-foreground">Values in Gold Weight (Grams)</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Formula Info Card */}
        <Card className="mb-4 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-700 mb-2">P&L Calculation Formulas (in Grams)</p>
                <div className="space-y-1 text-blue-600 font-mono text-xs">
                  <p><span className="text-green-600">Income on Sale</span> = (Millis Bought × 1.5%) ÷ 1000</p>
                  <p><span className="text-green-600">Income on Redemption</span> = (Millis Sold × 2% spread) ÷ 1000</p>
                  <p><span className="text-red-600">Expenses on Reward</span> = (Millis Allocated × 98.5%) ÷ 1000</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Date Filters */}
        <Card className="mb-6 border-gold/20">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs text-muted-foreground">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <Label className="text-xs text-muted-foreground">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => applyPreset('today')}>Today</Button>
                <Button size="sm" variant="outline" onClick={() => applyPreset('this_month')}>This Month</Button>
                <Button size="sm" variant="outline" onClick={() => applyPreset('this_fy')}>This FY</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-24" />
          </div>
        ) : report ? (
          <>
            {/* Income Section */}
            <Card className="mb-4 border-green-200 bg-green-50/30">
              <CardHeader>
                <CardTitle className="font-serif text-lg text-green-700 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Income (in Grams)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Income on Sale */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">Income on Sale (1.5% Spread)</p>
                      <p className="text-xs text-muted-foreground">
                        {report.income.on_sale.millis_bought?.toFixed(2)} Millis Bought × {report.income.on_sale.spread_percent}% ÷ 1000
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-xl text-green-600">{formatGrams(report.income.on_sale.grams)}</p>
                  </div>
                </div>

                {/* Income on Redemption */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">Income on Redemption ({report.income.on_redemption.rate_diff_percent}% Spread)</p>
                      <p className="text-xs text-muted-foreground">
                        {report.income.on_redemption.millis_sold?.toFixed(2)} Millis Sold × {report.income.on_redemption.rate_diff_percent}% ÷ 1000
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-xl text-green-600">{formatGrams(report.income.on_redemption.grams)}</p>
                  </div>
                </div>

                {/* Total Income */}
                <div className="flex items-center justify-between p-4 bg-green-100 rounded-lg">
                  <p className="font-semibold text-green-800">Total Income</p>
                  <p className="font-bold text-2xl text-green-700">{formatGrams(report.income.total_grams)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Expenses Section */}
            <Card className="mb-4 border-red-200 bg-red-50/30">
              <CardHeader>
                <CardTitle className="font-serif text-lg text-red-700 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5" />
                  Expenses (in Grams)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Expenses on Rewards */}
                <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <Gift className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-navy">Expenses on Rewards ({report.expenses.on_rewards.expense_percent}%)</p>
                      <p className="text-xs text-muted-foreground">
                        {report.expenses.on_rewards.millis_allocated?.toFixed(2)} Millis Allocated × {report.expenses.on_rewards.expense_percent}% ÷ 1000
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-xl text-red-600">{formatGrams(report.expenses.on_rewards.grams)}</p>
                  </div>
                </div>

                {/* Total Expenses */}
                <div className="flex items-center justify-between p-4 bg-red-100 rounded-lg">
                  <p className="font-semibold text-red-800">Total Expenses</p>
                  <p className="font-bold text-2xl text-red-700">{formatGrams(report.expenses.total_grams)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Net Profit/Loss */}
            <Card className={`border-2 ${netProfitGrams >= 0 ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Net {netProfitGrams >= 0 ? 'Profit' : 'Loss'}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {startDate} to {endDate}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold text-3xl ${netProfitGrams >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatGrams(Math.abs(netProfitGrams))}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      ≈ {formatCurrency(report.net_profit.inr_equivalent)} @ ₹{report.net_profit.gold_rate_used}/g
                    </p>
                    <Badge className={netProfitGrams >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {netProfitGrams >= 0 ? 'PROFIT' : 'LOSS'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <Card className="border-gold/20">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">Total Bookings</p>
                  <p className="font-bold text-xl text-navy">{report.summary.total_bookings}</p>
                  <p className="text-xs text-green-600">{formatCurrency(report.summary.total_bookings_value_inr)}</p>
                </CardContent>
              </Card>
              <Card className="border-gold/20">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">Total Refunds</p>
                  <p className="font-bold text-xl text-navy">{report.summary.total_refunds}</p>
                  <p className="text-xs text-orange-600">{formatCurrency(report.summary.total_refunds_value_inr)}</p>
                </CardContent>
              </Card>
              <Card className="border-gold/20">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">Total Redemptions</p>
                  <p className="font-bold text-xl text-navy">{report.summary.total_redemptions}</p>
                  <p className="text-xs text-purple-600">{report.summary.total_millis_redeemed?.toFixed(2)} Milli's</p>
                </CardContent>
              </Card>
              <Card className="border-gold/20">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-muted-foreground">Referral Rewards</p>
                  <p className="font-bold text-xl text-navy">{report.summary.referral_rewards_count}</p>
                  <p className="text-xs text-blue-600">Avg Rate: ₹{report.summary.avg_buy_rate?.toFixed(2)}</p>
                </CardContent>
              </Card>
            </div>
          </>
        ) : (
          <Card className="border-gold/20">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Select a date range to view the P&L report</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminProfitLossPage;
